import LogoutIcon from "@mui/icons-material/Logout";
import {
  AppBar,
  Box,
  Button,
  Container,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../auth/TokenContext";
import { brand } from "../theme";

const NAV = [
  { to: "/dashboard", label: "Inicio" },
  { to: "/merchants", label: "Comercios" },
  { to: "/invoices", label: "Facturas" },
  { to: "/providers", label: "Pasarelas" },
];

export function AdminLayout({ children }: { children: ReactNode }) {
  const { session, clear } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = NAV.find((n) => location.pathname.startsWith(n.to))?.to ?? false;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: brand.navy,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 76 }, gap: 1.5, px: { xs: 1.5, sm: 3 } }}>
          <Box
            component="img"
            src="/brand/logo-on-dark.png"
            alt="Novasis Pay"
            sx={{ height: { xs: 40, sm: 52 } }}
          />
          <Typography
            variant="caption"
            sx={{
              color: brand.textMutedOnDark,
              ml: 0.5,
              letterSpacing: 1.5,
              fontWeight: 700,
              display: { xs: "none", sm: "inline" },
            }}
          >
            ADMIN
          </Typography>
          <Box sx={{ flex: 1 }} />
          {session?.user && (
            <Stack
              direction="column"
              alignItems="flex-end"
              sx={{ mr: 0.5, display: { xs: "none", sm: "flex" } }}
            >
              <Typography variant="body2" sx={{ color: brand.textOnDark, fontWeight: 600, lineHeight: 1.2 }}>
                {session?.user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: brand.textMutedOnDark, lineHeight: 1.2 }}>
                {session?.user?.role}
              </Typography>
            </Stack>
          )}
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={() => {
              clear();
              navigate("/login", { replace: true });
            }}
            sx={{ color: brand.textOnDark, minWidth: 0 }}
          >
            Salir
          </Button>
        </Toolbar>
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => navigate(v)}
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                color: brand.textMutedOnDark,
                minHeight: 42,
                fontWeight: 600,
              },
              "& .Mui-selected": { color: "#fff" },
            }}
          >
            {NAV.map((n) => (
              <Tab key={n.to} value={n.to} label={n.label} />
            ))}
          </Tabs>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 }, flex: 1 }}>
        <Stack spacing={3}>{children}</Stack>
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: "center",
          color: "text.secondary",
          fontSize: 12,
          borderTop: "1px solid #E5E7EB",
        }}
      >
        Novasis Pay · Panel administrativo
      </Box>
    </Box>
  );
}
